import {
  deleteStockGroupResultSchema,
  slowMovingReportSchema,
  stockGroupListSchema,
  stockGroupSchema,
  stockItemSchema,
  stockListSchema,
  stockMovementListSchema,
  wastageReportSchema,
  type CreateStockGroupInput,
  type CreateStockItemInput,
  type CreateVariantInput,
  type DeleteStockGroupResult,
  type IssueToOrderInput,
  type RecordMovementInput,
  type ReturnToStockInput,
  type SlowMovingReport,
  type StockCategory,
  type StockGroup,
  type StockGroupList,
  type StockItem,
  type StockList,
  type StockMovementList,
  type UpdateStockGroupInput,
  type UpdateStockItemInput,
  type WastageReport,
} from '@tailoring-crm/shared';
import { z } from 'zod';
import type { ApiClient } from '../api/client.js';

/**
 * Every stock call the app makes.
 *
 * Note what is absent: there is no "set quantity". The API has no such route, so offering one
 * here would be offering a button that cannot work. Everything that changes a number is a
 * MOVEMENT with a reason, which is the whole point of the module.
 */

const STOCK = '/stock';

export interface StockListParams {
  q?: string;
  category?: StockCategory;
  /** One heading's items. Sent alongside the rest, never instead of them. */
  groupId?: string;
  lowOnly?: boolean;
  includeArchived?: boolean;
}

export async function getStockList(
  client: ApiClient,
  params: StockListParams = {},
): Promise<StockList> {
  return await client.get(STOCK, {
    schema: stockListSchema,
    query: {
      ...(params.q ? { q: params.q } : {}),
      ...(params.category ? { category: params.category } : {}),
      ...(params.groupId ? { groupId: params.groupId } : {}),
      ...(params.lowOnly ? { lowOnly: 'true' } : {}),
      ...(params.includeArchived ? { includeArchived: 'true' } : {}),
    },
  });
}

// --- Groups ---------------------------------------------------------------------------------

/**
 * The headings, and nothing about quantity. A group is a label — see the API's group service
 * for why it deliberately carries no total of its own.
 */
const GROUPS = `${STOCK}/groups`;

export interface StockGroupListParams {
  includeInactive?: boolean;
}

export async function getStockGroups(
  client: ApiClient,
  params: StockGroupListParams = {},
): Promise<StockGroupList> {
  return await client.get(GROUPS, {
    schema: stockGroupListSchema,
    query: { ...(params.includeInactive ? { includeInactive: 'true' } : {}) },
  });
}

export async function createStockGroup(
  client: ApiClient,
  input: CreateStockGroupInput,
): Promise<StockGroup> {
  return await client.post(GROUPS, input, { schema: stockGroupSchema });
}

export async function updateStockGroup(
  client: ApiClient,
  groupId: string,
  input: UpdateStockGroupInput,
): Promise<StockGroup> {
  return await client.patch(`${GROUPS}/${groupId}`, input, { schema: stockGroupSchema });
}

export async function deleteStockGroup(
  client: ApiClient,
  groupId: string,
): Promise<DeleteStockGroupResult> {
  return await client.del(`${GROUPS}/${groupId}`, undefined, {
    schema: deleteStockGroupResultSchema,
  });
}

export async function getStockItem(client: ApiClient, itemId: string): Promise<StockItem> {
  return await client.get(`${STOCK}/${itemId}`, { schema: stockItemSchema });
}

export async function createStockItem(
  client: ApiClient,
  input: CreateStockItemInput,
): Promise<StockItem> {
  return await client.post(STOCK, input, { schema: stockItemSchema });
}

export async function updateStockItem(
  client: ApiClient,
  itemId: string,
  input: UpdateStockItemInput,
): Promise<StockItem> {
  return await client.patch(`${STOCK}/${itemId}`, input, { schema: stockItemSchema });
}

export async function addVariant(
  client: ApiClient,
  itemId: string,
  input: CreateVariantInput,
): Promise<StockItem> {
  return await client.post(`${STOCK}/${itemId}/variants`, input, { schema: stockItemSchema });
}

export async function getMovements(
  client: ApiClient,
  itemId: string,
  variantId?: string,
): Promise<StockMovementList> {
  return await client.get(`${STOCK}/${itemId}/movements`, {
    schema: stockMovementListSchema,
    ...(variantId ? { query: { variantId } } : {}),
  });
}

/** The movement result is wide; only what the screens read is validated. */
const movementResultSchema = z.object({
  item: stockItemSchema,
  crossedReorderLevel: z.boolean(),
});
export type MovementOutcome = z.infer<typeof movementResultSchema>;

export async function recordMovement(
  client: ApiClient,
  itemId: string,
  input: RecordMovementInput,
): Promise<MovementOutcome> {
  return await client.post(`${STOCK}/${itemId}/movements`, input, {
    schema: movementResultSchema,
  });
}

const issueResultSchema = z.object({
  issue: movementResultSchema,
  materialCostPaise: z.number().int(),
});

export async function issueToOrder(
  client: ApiClient,
  itemId: string,
  input: IssueToOrderInput,
): Promise<z.infer<typeof issueResultSchema>> {
  return await client.post(`${STOCK}/${itemId}/issue`, input, { schema: issueResultSchema });
}

export async function returnToStock(
  client: ApiClient,
  itemId: string,
  input: ReturnToStockInput,
): Promise<MovementOutcome> {
  return await client.post(`${STOCK}/${itemId}/return`, input, { schema: movementResultSchema });
}

export async function getWastageReport(
  client: ApiClient,
  params: { from?: string; to?: string } = {},
): Promise<WastageReport> {
  return await client.get(`${STOCK}/reports/wastage`, {
    schema: wastageReportSchema,
    query: {
      ...(params.from ? { from: params.from } : {}),
      ...(params.to ? { to: params.to } : {}),
    },
  });
}

export async function getSlowMovingReport(
  client: ApiClient,
  sinceDays = 90,
): Promise<SlowMovingReport> {
  return await client.get(`${STOCK}/reports/slow-moving`, {
    schema: slowMovingReportSchema,
    query: { sinceDays },
  });
}
